import React, { useMemo } from 'react';
import SearchInput from '../../common/Input/SearchInput';
import Pagination from '../../common/Pagination/Pagination';
import LazyLoadImageProp from '../../common/LazyLoadImage';
import useExercises from '../hooks/useExercises';
import { formatExerciseDuration } from '../../../utils/helper';

const TYPE_META = {
  exercise: {
    apiType: 'Exercise',
    title: 'Select Existing Exercises',
    emptyText: 'No exercises found'
  },
  stretch: {
    apiType: 'Stretches',
    title: 'Select Existing Stretches',
    emptyText: 'No stretches found'
  }
};

const ExerciseSelectorPanel = ({ type = 'exercise', selectedItems = [], onAddItem, isView }) => {
  const meta = TYPE_META[type] || TYPE_META.exercise;
  const { exercises, loading, error, searchTerm, setSearchTerm, pagination, goToPage } =
    useExercises();

  const selectedIds = useMemo(
    () =>
      new Set(
        (selectedItems || []).map((item) => item.exercise_id?._id || item.exercise_id || item._id)
      ),
    [selectedItems]
  );

  const mappedExercises = useMemo(
    () =>
      (exercises || []).map((item) => ({
        exercise_id: item._id,
        _id: item._id,
        name: item.name,
        muscle_groups: item.muscle_groups || [],
        category_ids: (item.category_ids || []).map(
          (category) => category._id || category.value || category
        ),
        exercise_duration: item.exercise_duration || 0,
        video_url: item.video_url || '',
        image_url: item.image_url || '',
        difficulty: item.difficulty || '',
        metadata: { sets: [] }
      })),
    [exercises]
  );

  return (
    <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg p-6">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{meta.title}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Search and add existing {type === 'exercise' ? 'exercises' : 'stretches'} to this
            workout.
          </p>
        </div>
      </div>

      <div className="mb-6">
        <SearchInput
          id={`${type}-search`}
          name={`${type}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={`Search ${type === 'exercise' ? 'exercises' : 'stretches'}`}
        />
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-shoorah-primary"></div>
        </div>
      )}

      {!loading && mappedExercises.length === 0 && (
        <div className="border border-dashed border-gray-300 rounded-lg py-12 px-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">{meta.emptyText}</p>
        </div>
      )}

      {!loading && mappedExercises.length > 0 && (
        <div className="space-y-3">
          {mappedExercises.map((item) => {
            const isAdded = selectedIds.has(item.exercise_id);

            return (
              <div
                key={item.exercise_id}
                className="flex items-center gap-4 border border-gray-200 dark:border-shoorah-darkBorder rounded-xl p-4"
              >
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                  {item.image_url ? (
                    <LazyLoadImageProp imageSrc={item.image_url} className="w-16 h-16" />
                  ) : (
                    <div className="w-16 h-16 flex items-center justify-center text-xs text-gray-400">
                      No Image
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {item.name}
                    </h4>
                    {item.difficulty && (
                      <span className="px-2 py-1 text-[11px] rounded-full bg-gray-100 text-gray-600 dark:bg-shoorah-darkBgColor dark:text-gray-300">
                        {item.difficulty}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {formatExerciseDuration(item.exercise_duration) || '—'}
                  </p>
                  {item.muscle_groups?.length > 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                      {item.muscle_groups.join(', ')}
                    </p>
                  )}
                </div>

                {!isView && (
                  <button
                    type="button"
                    disabled={isAdded}
                    onClick={() => onAddItem(item)}
                    className="btn-primary btn-sm"
                  >
                    {isAdded ? 'Added' : 'Add'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!!error && !loading && <span className="text-red-500 text-[12px] mt-3 block">{error}</span>}

      {!loading && pagination.total > pagination.limit && (
        <Pagination
          currentPage={pagination.page}
          totalCount={pagination.total}
          pageSize={pagination.limit}
          onPageChange={goToPage}
          disabled={loading}
        />
      )}
    </div>
  );
};

export default ExerciseSelectorPanel;
